import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import onboardingIcon from 'assets/images/onboarding-handshake.svg';
import { BUDDY_TOUR_EVENT } from 'config/buddy/tour';
import { ChatInputStepHeader } from 'features/chat/components/chat-input-step-header';
import { ActionPromptCard } from 'features/chat/components/action-prompt-card';
import { addressContext } from 'features/conversation-preferences/address-form';
import { useAddressForm } from 'features/conversation-preferences/hooks/use-conversation-preferences';
import { answerOnboardingPrompt, onboardingQuestionFor } from '../onboarding-chat';
import { onboardingCompleteMessage } from '../onboarding-script';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  ONBOARDING_INDUSTRY_STEP,
  ONBOARDING_TOTAL_STEPS,
  useOnboardingDoneElsewhere,
  useOnboardingJourney,
} from '../onboarding-journey';
import { OnboardingStepCard, type CommonCardProps } from './onboarding-step-card';
import {
  isJourneyDone,
  progressOf,
  useJourneyExit,
  useJourneyFinish,
  useOnboardingTranscript,
  useStarterChat,
} from './use-onboarding-panel';

/** The catch-up asks two things: the form (with the salutation) and the industry. */
const CATCH_UP_TOTAL_STEPS = 2;

export interface OnboardingChatPanelProps {
  disabled?: boolean;
}

/** Chat-page onboarding UI: step card + step header above the composer.
 *  Renders nothing unless the route has ?journey=onboarding. */
export function OnboardingChatPanel({ disabled }: Readonly<OnboardingChatPanelProps>) {
  const { t, i18n } = useTranslation();
  const language = i18n?.language;
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const { isOnboarding, step, answers, setStep } = useOnboardingJourney();
  const userId = useCommonStore((state) => state.user?.id);
  const addressForm = useAddressForm();
  const isDoneElsewhere = useOnboardingDoneElsewhere();
  const isCatchUp = Boolean(answers.catchUp);
  const isDone = isJourneyDone(answers);

  useOnboardingTranscript(conversationId, step, answers, language, userId);
  const leavesJourney = useJourneyExit({
    isOnboarding,
    isDone,
    isDoneElsewhere,
    conversationId,
    navigate,
  });
  const { finishOnboarding, skipCatchUp } = useJourneyFinish({
    conversationId,
    answers,
    userId,
    navigate,
  });
  const { isPreparingChat, startChat } = useStarterChat({
    disabled,
    language,
    addressForm,
    answers,
    finishOnboarding,
    conversationId,
    step,
    userId,
    isOnboarding,
  });

  if (!isOnboarding || !step || leavesJourney) return null;

  const answer = (text: string) => {
    if (conversationId) void answerOnboardingPrompt(conversationId, step, isDone, text, setStep);
  };

  const skipProps = isCatchUp
    ? { skipLabel: t('ONBOARDING_CATCHUP_SKIP', { defaultValue: 'Later' }), onSkip: skipCatchUp }
    : {};
  const commonCardProps: CommonCardProps = {
    otherLabel: t('SOMETHING_ELSE', { defaultValue: 'Something Else' }),
    disabled,
    ariaLabel: onboardingQuestionFor(step, progressOf(answers)),
    ...skipProps,
  };

  const startTour = () => {
    finishOnboarding();
    /* Same event the old header tour button used. */
    globalThis.dispatchEvent(new Event(BUDDY_TOUR_EVENT));
  };

  // Back goes as far as the industry; the name and salutation are answered once.
  const canGoBack = !isCatchUp && step > ONBOARDING_INDUSTRY_STEP && !disabled && !isPreparingChat;
  const preparingLabel = t('ONBOARDING_PREPARING_CHAT', {
    defaultValue: "One moment, I'm getting everything ready …",
    context: addressContext(addressForm),
  });

  return (
    <div className="flex w-full flex-col gap-6">
      <OnboardingStepCard
        step={step}
        isDone={isDone}
        disabled={disabled}
        conversationId={conversationId}
        answers={answers}
        language={language}
        commonCardProps={commonCardProps}
        answer={answer}
        setStep={setStep}
      />

      {isDone ? (
        <ActionPromptCard
          message={onboardingCompleteMessage(addressForm)}
          actions={[
            {
              label: t('GIVE_ME_A_TOUR', { defaultValue: 'Give me a tour' }),
              onClick: startTour,
              variant: 'outline',
              className: 'hidden sm:inline-flex',
            },
            {
              label: isPreparingChat
                ? preparingLabel
                : t('START_CREATING', { defaultValue: 'Start Creating' }),
              onClick: startChat,
            },
          ]}
          disabled={disabled || isPreparingChat}
        />
      ) : null}

      <ChatInputStepHeader
        icon={<img src={onboardingIcon} alt="" className="size-5 dark:invert" />}
        label={t('ONBOARDING', { defaultValue: 'Onboarding' })}
        step={isCatchUp ? Math.min(step, CATCH_UP_TOTAL_STEPS) : step}
        totalSteps={isCatchUp ? CATCH_UP_TOTAL_STEPS : ONBOARDING_TOTAL_STEPS}
        onPrev={canGoBack ? () => setStep(step - 1) : undefined}
      />
    </div>
  );
}
